import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/universal/header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamClearButton,
  ParamDataTable,
  ParamDataTableToolbar,
  ParamSearchFilter,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import {
  getGetEntitiesQueryOptions,
  useGetEntities,
} from "@/requests/gen/react-query/entity";
import { EntityTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { keepPreviousData } from "@tanstack/react-query";
import { linkOptions, type LinkProps } from "@tanstack/react-router";
import { ColumnDef } from "@tanstack/react-table";
import { useQueryStates, type inferParserType } from "nuqs";

type VendorSearch = inferParserType<typeof dataTableSearchParsers>;

function vendorLookupQueryParams(
  p: VendorSearch,
): Parameters<typeof getGetEntitiesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    entity_types: [EntityTypeEnum.Fulfiller],
    include_visible_entities: "can_see_entities",
  };
}

export const Route = createFileRoute(
  "/_private/_inventory-360/inventory-vendor/lookup/",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetEntitiesQueryOptions(
        vendorLookupQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: InventoryVendorLookup,
});

interface EntityTableProps {
  link?: LinkProps;
  name: string;
  email: string;
  npi_number: string;
}

const columns: ColumnDef<EntityTableProps>[] = [
  { header: "Name", accessorKey: "name" },
  { header: "Email", accessorKey: "email" },
  { header: "NPI Number", accessorKey: "npi_number" },
];

function InventoryVendorLookup() {
  const [queryStates] = useQueryStates(dataTableSearchParsers);

  const entities = useGetEntities(vendorLookupQueryParams(queryStates), {
    query: { placeholderData: keepPreviousData },
  });

  const tableData: EntityTableProps[] | undefined = entities.data?.items.map(
    (entity) => ({
      link: linkOptions({
        to: "/entity/$entityId",
        params: { entityId: String(entity.entity_id) },
      }),
      name: entity.name,
      email: entity.email || "",
      npi_number: entity.npi_number || "",
    }),
  );

  return (
    <div>
      <title>Vendors - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Vendors", link: { to: "/inventory-vendor/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Vendors</CardTitle>
          <CardDescription>
            View vendor entities. These are fulfillers visible to your
            practices.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={entities.isFetching} />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={entities.isLoading}
            isError={entities.isError}
            count={entities.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
